import React from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getAIFeedbacks } from 'apis/FeedbackAPI';
import { AI_FEEDBACK_FEATURE_TYPES } from 'components/Feedback/constants';
import Feedback from 'components/Feedback/Feedback';
import { AICompetitiveIntelligenceRelatedDataType } from 'types/recommendations';

const RANKING_BASED_ON_LABEL = 'Ranking based on:';

type Props = {
  communityId?: number;
  categoryLabel?: string;
  relatedData?: AICompetitiveIntelligenceRelatedDataType;
};

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 12px;
  margin-bottom: 28px;
`;

const Sources = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SourceChip = styled.span`
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--text-secondary);
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1;
`;

const CategoryRankingMeta = ({ communityId, categoryLabel, relatedData }: Props) => {
  const recommendationOverviewId = relatedData?.recommendation_overview_id;
  const dataSources = relatedData?.data_sources ?? [];

  const { data: feedback } = useQuery({
    queryKey: ['ai-feedbacks', 'recommendation-overview', recommendationOverviewId, communityId],
    queryFn: () =>
      getAIFeedbacks({
        recommendation_overview_id: recommendationOverviewId as number,
        community_id: communityId,
      }),
    select: (feedbacks) => feedbacks?.[0] ?? null,
    enabled: recommendationOverviewId != null && Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  if (dataSources.length === 0 && recommendationOverviewId == null) {
    return null;
  }

  return (
    <Container data-test-id="ai-ci-category-ranking-meta">
      <Sources>
        {dataSources.length > 0 && <Label>{RANKING_BASED_ON_LABEL}</Label>}
        {dataSources.map((source) => (
          <SourceChip key={source.label} title={source.tooltip ?? undefined}>
            {source.label}
          </SourceChip>
        ))}
      </Sources>
      {recommendationOverviewId != null && (
        <Feedback
          subtitle={categoryLabel ?? RANKING_BASED_ON_LABEL}
          featureType={AI_FEEDBACK_FEATURE_TYPES.CATEGORY_SCORING}
          recommendationOverview={recommendationOverviewId}
          sourceUrl={window.location.href}
          existingFeedback={feedback}
        />
      )}
    </Container>
  );
};

export default CategoryRankingMeta;
